import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  PiGasPump,
  PiWrench,
  PiCarProfile,
  PiBank,
  PiSquaresFour,
  PiShieldCheck,
  PiPhone,
  PiLetterCircleP,
  PiDrop,
  PiRoadHorizon,
  PiTire,
  PiTShirt,
  PiToolbox,
  PiShield,
  PiScales
} from "react-icons/pi";

import { useExpensesFormStore } from "@/store/useExpensesFormStore";
import { useTourStore } from "@/store/useTourStore";
import { ExpenseCategory } from "@/types/expense";
import CategoryGrid, { CategoryGridItem } from "@/components/common/CategoryGrid/CategoryGrid";

interface ExpenseGridItem extends CategoryGridItem {
  category: ExpenseCategory;
}

const ExpensesCategoryGrid = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setExpensesForm = useExpensesFormStore((state) => state.setForm);
  const expensesDisclaimerAcknowledged = useTourStore(
    (state) => state.expensesDisclaimerAcknowledged
  );

  const handleCategoryClick = async (item: CategoryGridItem) => {
    const expenseItem = item as ExpenseGridItem;

    await setExpensesForm({
      category: expenseItem.category
    });

    if (!expensesDisclaimerAcknowledged) {
      navigate('/expenses/disclaimer');
    } else {
      navigate('/expenses/create');
    }
  };
  
  const driverExpenses: ExpenseGridItem[] = [
    {
      id: "fuel",
      title: t('expenses.grid.fuel'),
      subtitle: t('expenses.grid.fuelDesc'),
      icon: <PiGasPump className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory,
      className: "tour-category-item"
    },
    {
      id: "platform_commission",
      title: t('expenses.grid.platformCommission'),
      subtitle: t('expenses.grid.platformCommissionDesc'),
      icon: <PiBank className="h-4 w-4 text-zinc-700" />,
      category: "commission_fees" as ExpenseCategory
    },
    {
      id: "rental",
      title: t('expenses.grid.rental'),
      subtitle: t('expenses.grid.rentalDesc'),
      icon: <PiCarProfile className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "other",
      title: t('expenses.grid.other'),
      subtitle: t('expenses.grid.otherDesc'),
      icon: <PiSquaresFour className="h-4 w-4 text-zinc-700" />,
      category: "other_expenses" as ExpenseCategory
    },
    {
      id: "maintenance",
      title: t('expenses.grid.maintenance'),
      subtitle: t('expenses.grid.maintenanceDesc'),
      icon: <PiWrench className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "parking",
      title: t('expenses.grid.parking'),
      subtitle: t('expenses.grid.parkingDesc'),
      icon: <PiLetterCircleP className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "car_wash",
      title: t('expenses.grid.carWash'),
      subtitle: t('expenses.grid.carWashDesc'),
      icon: <PiDrop className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "insurance",
      title: t('expenses.grid.insurance'),
      subtitle: t('expenses.grid.insuranceDesc'),
      icon: <PiShieldCheck className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "phone",
      title: t('expenses.grid.phone'),
      subtitle: t('expenses.grid.phoneDesc'),
      icon: <PiPhone className="h-4 w-4 text-zinc-700" />,
      category: "communication_expenses" as ExpenseCategory
    },
    {
      id: "psd",
      title: t('expenses.grid.psd'),
      subtitle: t('expenses.grid.psdDesc'),
      icon: <PiShield className="h-4 w-4 text-zinc-700" />,
      category: "taxes_and_fees" as ExpenseCategory
    },
    {
      id: "vsd",
      title: t('expenses.grid.vsd'),
      subtitle: t('expenses.grid.vsdDesc'),
      icon: <PiShield className="h-4 w-4 text-zinc-700" />,
      category: "taxes_and_fees" as ExpenseCategory
    },
    {
      id: "pvm",
      title: t('expenses.grid.pvm'),
      subtitle: t('expenses.grid.pvmDesc'),
      icon: <PiScales className="h-4 w-4 text-zinc-700" />,
      category: "taxes_and_fees" as ExpenseCategory
    },
    {
      id: "tolls",
      title: t('expenses.grid.tolls'),
      subtitle: t('expenses.grid.tollsDesc'),
      icon: <PiRoadHorizon className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "tires",
      title: t('expenses.grid.tires'),
      subtitle: t('expenses.grid.tiresDesc'),
      icon: <PiTire className="h-4 w-4 text-zinc-700" />,
      category: "vehicle_expenses" as ExpenseCategory
    },
    {
      id: "work_clothes",
      title: t('expenses.grid.workClothes'),
      subtitle: t('expenses.grid.workClothesDesc'),
      icon: <PiTShirt className="h-4 w-4 text-zinc-700" />,
      category: "equipment_and_supplies" as ExpenseCategory
    },
    {
      id: "tools",
      title: t('expenses.grid.tools'),
      subtitle: t('expenses.grid.toolsDesc'),
      icon: <PiToolbox className="h-4 w-4 text-zinc-700" />,
      category: "equipment_and_supplies" as ExpenseCategory
    }
  ];

  return (
    <CategoryGrid
      title={t('expenses.grid.title')}
      items={driverExpenses}
      onItemClick={handleCategoryClick}
    />
  );
};

export default ExpensesCategoryGrid;
